import { html, nothing } from 'lit';
import { translate } from 'lit-translate';
import { appState } from '../app.state';
import { actionRegistry } from '../actions/action-registry';
import type { ActionId } from '../actions/action.types';
import type { TimelineToolbarItem } from '../types/timeline-toolbar-item.interface';

/**
 * Configuration for a simple toolbar button created via the factory.
 */
interface SimpleButtonConfig {
    /** Unique toolbar item ID. */
    id: string;
    /**
     * CSS class(es) for the button (e.g., 'focus-today', 'toggle-week-view'). A function is
     * called on every render — for a toggle whose icon follows its state.
     */
    cssClass: string | (() => string);
    /**
     * i18n key for the button label, read via translate() for reactive updates. A function is
     * called on every render — for a toggle whose label names what the next click does.
     */
    labelKey: string | (() => string);
    /** Action to execute when the button is clicked. */
    actionId: ActionId;
    /** Optional payload for the action (e.g. the cell-width view of that button). */
    actionPayload?: unknown;
    /** Position in the toolbar: 'start' (left) or 'end' (right). */
    position: 'start' | 'end';
    /** Sort order within position group (default: 0). */
    order?: number;
}

/**
 * Creates a simple toolbar button item that uses the Action-Registry for click handling.
 *
 * Renders a `<button>` with:
 * - Combined CSS classes: `{cssClass} buttonform-marker secondary topbar-element`
 * - `data-label` attribute set to the translated label string (via translate() directive for reactive CSS ::after)
 * - A `<div class="hover-effect">` child element (required by designer CSS)
 * - A click handler that calls `actionRegistry.execute(actionId, actionPayload)`
 * - Login guard: renders nothing when the user is not logged in
 *
 * `cssClass` and `labelKey` may be functions; views re-render on appState changes through
 * their StateController, so a toggle's label and icon follow its state.
 *
 * @param config - Configuration for the button.
 * @returns A TimelineToolbarItem ready for use in defaultTimelineToolbarItems().
 */
export function createSimpleButtonToolbarItem(config: SimpleButtonConfig): TimelineToolbarItem {
    return {
        id: config.id,
        position: config.position,
        order: config.order ?? 0,
        render: () => {
            if (!appState.isUserLoggedIn) {
                return nothing;
            }
            const cssClass = typeof config.cssClass === 'function' ? config.cssClass() : config.cssClass;
            const labelKey = typeof config.labelKey === 'function' ? config.labelKey() : config.labelKey;
            return html`
                <button type="button"
                        class="${cssClass} buttonform-marker secondary topbar-element"
                        data-label="${translate(labelKey)}"
                        @click=${() => actionRegistry.execute(config.actionId, config.actionPayload)}>
                    <div class="hover-effect"></div>
                </button>
            `;
        },
    };
}
